import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Plus, Send, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { apiClient, ADMIN_TOKEN_KEY } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ADMIN } from '../constants/testIds';

const emptyCampaign = { name: '', subject: '', headline: '', body: '', cta_label: '', cta_url: '', audience: 'leads' };

export default function AdminPage() {
    const { user, loading } = useAuth();
    const [adminSession, setAdminSession] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY));
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [audienceCounts, setAudienceCounts] = useState({ leads: 0, customers: 0, all: 0 });
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [campaignOpen, setCampaignOpen] = useState(false);
    const [draft, setDraft] = useState({ code: '', type: 'percent', value: 10, max_uses: '', expires_at: '', description: '' });
    const [campaign, setCampaign] = useState(emptyCampaign);
    const [previewHtml, setPreviewHtml] = useState('');
    const [sending, setSending] = useState(false);

    const refresh = async () => {
        try {
            const [s, l, r, c, camp, ac] = await Promise.all([
                apiClient.get('/admin/stats'),
                apiClient.get('/leads'),
                apiClient.get('/reservations'),
                apiClient.get('/admin/coupons'),
                apiClient.get('/admin/campaigns'),
                apiClient.get('/admin/campaigns/audience-counts'),
            ]);
            setStats(s.data); setLeads(l.data); setReservations(r.data); setCoupons(c.data);
            setCampaigns(camp.data); setAudienceCounts(ac.data);
        } catch (e) {
            setError(e?.response?.status === 403 ? 'Admin access required.' : 'Failed to load.');
        }
    };

    useEffect(() => {
        if (loading) return;
        if (!user && !adminSession) return;
        refresh();
    }, [user, loading, adminSession]);

    const createCoupon = async () => {
        if (!draft.code || draft.value <= 0) { toast.error('Code + positive value required.'); return; }
        try {
            const payload = {
                code: draft.code,
                type: draft.type,
                value: Number(draft.value),
                description: draft.description || undefined,
                max_uses: draft.max_uses ? Number(draft.max_uses) : undefined,
                expires_at: draft.expires_at ? new Date(draft.expires_at).toISOString() : undefined,
            };
            await apiClient.post('/admin/coupons', payload);
            toast.success(`Coupon ${draft.code.toUpperCase()} created.`);
            setOpen(false);
            setDraft({ code: '', type: 'percent', value: 10, max_uses: '', expires_at: '', description: '' });
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Create failed.');
        }
    };

    const deleteCoupon = async (code) => {
        if (!confirm(`Deactivate coupon ${code}?`)) return;
        try { await apiClient.delete(`/admin/coupons/${code}`); refresh(); } catch (_) { toast.error('Failed.'); }
    };

    const previewCampaign = async () => {
        if (!campaign.headline || !campaign.body) {
            toast.error('Headline and body are required to preview.');
            return;
        }
        try {
            const { data } = await apiClient.post('/admin/campaigns/preview', {
                headline: campaign.headline,
                body: campaign.body,
                cta_label: campaign.cta_label || undefined,
                cta_url: campaign.cta_url || undefined,
            });
            setPreviewHtml(data.html);
        } catch (_) { toast.error('Preview failed.'); }
    };

    const sendCampaign = async () => {
        if (!campaign.name || !campaign.subject || !campaign.headline || !campaign.body) {
            toast.error('Name, subject, headline and body are required.');
            return;
        }
        const recipients = audienceCounts[campaign.audience] || 0;
        if (recipients === 0) { toast.error('No recipients in this audience yet.'); return; }
        if (!confirm(`Send "${campaign.name}" to ${recipients} recipient(s)?`)) return;
        setSending(true);
        try {
            const { data } = await apiClient.post('/admin/campaigns', {
                ...campaign,
                cta_label: campaign.cta_label || undefined,
                cta_url: campaign.cta_url || undefined,
            });
            if (data.status === 'sent') toast.success(`Sent to ${data.sent_count} recipient(s).`);
            else if (data.status === 'partial') toast.success(`Sent ${data.sent_count}, ${data.failed_count} failed.`);
            else if (data.status === 'skipped') toast.message('Campaign logged but not sent — RESEND_API_KEY not configured.');
            else toast.error(`Send failed (${data.failed_count} errors).`);
            setCampaignOpen(false);
            setCampaign(emptyCampaign);
            setPreviewHtml('');
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Send failed.');
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-white/60">Loading…</div>;
    if (!user && !adminSession) return <AdminLogin onSuccess={(tok) => { localStorage.setItem(ADMIN_TOKEN_KEY, tok); setAdminSession(tok); }} />;
    if (error) return <div className="max-w-2xl mx-auto py-24 text-center text-white/60">{error}</div>;

    return (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14" data-testid={ADMIN.page}>
            <div className="mb-10 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-2">Admin</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">Control room</h1>
                </div>
                {adminSession && (
                    <button
                        data-testid="admin-logout"
                        onClick={() => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAdminSession(null); toast.message('Signed out.'); }}
                        className="text-xs uppercase tracking-widest text-white/50 hover:text-brand-pink border border-white/10 hover:border-brand-pink/40 rounded-full px-4 h-9"
                    >
                        Sign out
                    </button>
                )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid={ADMIN.stats}>
                <Stat k="Leads" v={stats?.leads ?? '—'} c="text-brand-aqua" />
                <Stat k="Reservations" v={stats?.reservations ?? '—'} c="text-brand-gold" />
                <Stat k="Confirmed" v={stats?.confirmed ?? '—'} c="text-brand-pink" />
                <Stat k="Revenue" v={stats ? `$${stats.revenue.toLocaleString()}` : '—'} c="text-brand-orange" />
                <Stat k="Active coupons" v={stats?.active_coupons ?? '—'} c="text-brand-aqua" />
            </div>

            <Tabs defaultValue="leads" className="space-y-5">
                <TabsList className="bg-brand-surface border border-white/5">
                    <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
                    <TabsTrigger value="reservations" data-testid="admin-tab-reservations">Reservations</TabsTrigger>
                    <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Coupons</TabsTrigger>
                    <TabsTrigger value="campaigns" data-testid="admin-tab-campaigns">Campaigns</TabsTrigger>
                </TabsList>

                <TabsContent value="leads">
                    <div data-testid={ADMIN.leads} className="bg-brand-surface border border-white/5 rounded-2xl overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-widest text-white/40">
                                <tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Destination</Th><Th>Budget</Th><Th>Travelers</Th><Th>When</Th></tr>
                            </thead>
                            <tbody>
                                {leads.length === 0 && <tr><td colSpan={7} className="text-center text-white/40 py-8">No leads yet.</td></tr>}
                                {leads.map((l) => (
                                    <tr key={l.id} className="border-t border-white/5">
                                        <Td>{l.name}</Td><Td>{l.email}</Td><Td>{l.phone || '—'}</Td><Td>{l.destination || '—'}</Td><Td>{l.budget || '—'}</Td><Td>{l.travelers || '—'}</Td><Td className="text-white/50">{new Date(l.created_at).toLocaleDateString()}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="reservations">
                    <div data-testid={ADMIN.reservations} className="bg-brand-surface border border-white/5 rounded-2xl overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-widest text-white/40">
                                <tr><Th>ID</Th><Th>Customer</Th><Th>Destination</Th><Th>Departure</Th><Th>Package</Th><Th>Guests</Th><Th>Total</Th><Th>Status</Th></tr>
                            </thead>
                            <tbody>
                                {reservations.length === 0 && <tr><td colSpan={8} className="text-center text-white/40 py-8">No reservations yet.</td></tr>}
                                {reservations.map((r) => (
                                    <tr key={r.id} className="border-t border-white/5">
                                        <Td className="font-mono text-xs text-brand-aqua">{r.id}</Td><Td>{r.contact_email}</Td><Td>{r.destination}</Td><Td>{r.departure_date}</Td><Td>{r.package_name}</Td><Td>{r.guests}</Td><Td className="text-brand-gold">${r.total_amount.toFixed(2)}</Td><Td>{r.status}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="coupons">
                    <div className="flex justify-end mb-4">
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="admin-create-coupon" className="btn-sunset rounded-full h-10 px-5 text-xs uppercase tracking-widest font-semibold"><Plus size={14} className="mr-1" /> New Coupon</Button>
                            </DialogTrigger>
                            <DialogContent className="bg-brand-surface border-white/10">
                                <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Create coupon</DialogTitle></DialogHeader>
                                <div className="space-y-4">
                                    <Field label="Code *"><Input data-testid="coupon-code" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} className="bg-transparent border-white/15 uppercase tracking-wider" placeholder="VIBE10" /></Field>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="Type">
                                            <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                                                <SelectTrigger data-testid="coupon-type" className="bg-transparent border-white/15"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="percent">% Percent off</SelectItem>
                                                    <SelectItem value="fixed">$ Fixed amount</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="Value *"><Input data-testid="coupon-value" type="number" min={0} value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} className="bg-transparent border-white/15" /></Field>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="Max uses"><Input type="number" min={1} value={draft.max_uses} onChange={(e) => setDraft({ ...draft, max_uses: e.target.value })} className="bg-transparent border-white/15" placeholder="Unlimited" /></Field>
                                        <Field label="Expires"><Input type="date" value={draft.expires_at} onChange={(e) => setDraft({ ...draft, expires_at: e.target.value })} className="bg-transparent border-white/15" /></Field>
                                    </div>
                                    <Field label="Description"><Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="bg-transparent border-white/15" placeholder="10% off any cruise" /></Field>
                                </div>
                                <DialogFooter><Button data-testid="coupon-submit" onClick={createCoupon} className="btn-sunset rounded-full h-10 px-6 uppercase tracking-widest font-semibold">Create</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <div data-testid="admin-coupons" className="bg-brand-surface border border-white/5 rounded-2xl overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-widest text-white/40">
                                <tr><Th>Code</Th><Th>Type</Th><Th>Value</Th><Th>Used</Th><Th>Max</Th><Th>Expires</Th><Th>Status</Th><Th></Th></tr>
                            </thead>
                            <tbody>
                                {coupons.length === 0 && <tr><td colSpan={8} className="text-center text-white/40 py-8">No coupons yet.</td></tr>}
                                {coupons.map((c) => (
                                    <tr key={c.code} className="border-t border-white/5">
                                        <Td className="font-mono text-brand-aqua">{c.code}</Td>
                                        <Td>{c.type}</Td>
                                        <Td className="text-brand-gold">{c.type === 'percent' ? `${c.value}%` : `$${c.value}`}</Td>
                                        <Td>{c.used_count || 0}</Td>
                                        <Td>{c.max_uses || '∞'}</Td>
                                        <Td className="text-white/60">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</Td>
                                        <Td><span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${c.active ? 'bg-brand-aqua/15 text-brand-aqua' : 'bg-white/5 text-white/40'}`}>{c.active ? 'Active' : 'Disabled'}</span></Td>
                                        <Td>{c.active && <button data-testid={`coupon-delete-${c.code}`} onClick={() => deleteCoupon(c.code)} className="text-white/40 hover:text-brand-pink"><Trash2 size={14} /></button>}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="campaigns">
                    <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-white/45">Reach</div>
                            <div className="font-display text-sm mt-1">
                                <span className="text-brand-aqua font-bold">{audienceCounts.leads}</span> leads ·{' '}
                                <span className="text-brand-gold font-bold">{audienceCounts.customers}</span> customers ·{' '}
                                <span className="text-brand-pink font-bold">{audienceCounts.all}</span> total
                            </div>
                        </div>
                        <Dialog open={campaignOpen} onOpenChange={(v) => { setCampaignOpen(v); if (!v) { setCampaign(emptyCampaign); setPreviewHtml(''); } }}>
                            <DialogTrigger asChild>
                                <Button data-testid="admin-new-campaign" className="btn-sunset rounded-full h-10 px-5 text-xs uppercase tracking-widest font-semibold">
                                    <Mail size={14} className="mr-1.5" /> New Campaign
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-brand-surface border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="font-display uppercase tracking-tight">Create email campaign</DialogTitle>
                                </DialogHeader>
                                <div className="grid lg:grid-cols-2 gap-5">
                                    <div className="space-y-4">
                                        <Field label="Internal name *">
                                            <Input data-testid="campaign-name" value={campaign.name} onChange={(e) => setCampaign({ ...campaign, name: e.target.value })} placeholder="Spring flash sale" className="bg-transparent border-white/15" />
                                        </Field>
                                        <Field label="Audience">
                                            <Select value={campaign.audience} onValueChange={(v) => setCampaign({ ...campaign, audience: v })}>
                                                <SelectTrigger data-testid="campaign-audience" className="bg-transparent border-white/15"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="leads">Leads ({audienceCounts.leads})</SelectItem>
                                                    <SelectItem value="customers">Customers ({audienceCounts.customers})</SelectItem>
                                                    <SelectItem value="all">Everyone ({audienceCounts.all})</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="Subject line *">
                                            <Input data-testid="campaign-subject" value={campaign.subject} onChange={(e) => setCampaign({ ...campaign, subject: e.target.value })} placeholder="15% off Caribbean — this weekend only" className="bg-transparent border-white/15" />
                                        </Field>
                                        <Field label="Headline *">
                                            <Input data-testid="campaign-headline" value={campaign.headline} onChange={(e) => setCampaign({ ...campaign, headline: e.target.value })} placeholder="Flash sale: 15% off Caribbean" className="bg-transparent border-white/15" />
                                        </Field>
                                        <Field label="Body *">
                                            <Textarea
                                                data-testid="campaign-body"
                                                value={campaign.body}
                                                onChange={(e) => setCampaign({ ...campaign, body: e.target.value })}
                                                rows={6}
                                                placeholder={"Hey there —\n\nFor 72 hours only, every Caribbean cruise is 15% off. Use code SPRING15 at checkout.\n\nYour Cruise Vibes concierge"}
                                                className="bg-transparent border-white/15 resize-none"
                                            />
                                        </Field>
                                        <div className="grid grid-cols-2 gap-3">
                                            <Field label="CTA label">
                                                <Input value={campaign.cta_label} onChange={(e) => setCampaign({ ...campaign, cta_label: e.target.value })} placeholder="Book now" className="bg-transparent border-white/15" />
                                            </Field>
                                            <Field label="CTA URL">
                                                <Input value={campaign.cta_url} onChange={(e) => setCampaign({ ...campaign, cta_url: e.target.value })} placeholder="https://…" className="bg-transparent border-white/15" />
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs uppercase tracking-widest text-white/50">Live preview</Label>
                                            <button data-testid="campaign-preview" type="button" onClick={previewCampaign} className="text-xs uppercase tracking-widest text-brand-aqua hover:text-brand-gold">
                                                Refresh preview
                                            </button>
                                        </div>
                                        <div className="border border-white/10 rounded-2xl bg-[#070B19] overflow-hidden h-[420px]">
                                            {previewHtml ? (
                                                <iframe
                                                    data-testid="campaign-preview-frame"
                                                    title="Campaign preview"
                                                    srcDoc={previewHtml}
                                                    className="w-full h-full bg-[#070B19]"
                                                />
                                            ) : (
                                                <div className="h-full flex items-center justify-center text-white/40 text-sm text-center px-6">
                                                    Click &ldquo;Refresh preview&rdquo; after filling the form to see how the email will look.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter className="mt-4">
                                    <Button data-testid="campaign-send" onClick={sendCampaign} disabled={sending} className="btn-sunset rounded-full h-11 px-7 uppercase tracking-widest font-semibold">
                                        <Send size={14} className="mr-2" /> {sending ? 'Sending…' : `Send to ${audienceCounts[campaign.audience] || 0} recipient(s)`}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <div data-testid="admin-campaigns-table" className="bg-brand-surface border border-white/5 rounded-2xl overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-widest text-white/40">
                                <tr><Th>Name</Th><Th>Subject</Th><Th>Audience</Th><Th>Recipients</Th><Th>Sent</Th><Th>Failed</Th><Th>Status</Th><Th>Sent at</Th></tr>
                            </thead>
                            <tbody>
                                {campaigns.length === 0 && <tr><td colSpan={8} className="text-center text-white/40 py-8">No campaigns yet. Create one to broadcast to your audience.</td></tr>}
                                {campaigns.map((c) => (
                                    <tr key={c.id} className="border-t border-white/5">
                                        <Td className="font-medium">{c.name}</Td>
                                        <Td className="text-white/70 max-w-[260px] truncate">{c.subject}</Td>
                                        <Td className="capitalize">{c.audience}</Td>
                                        <Td>{c.recipient_count}</Td>
                                        <Td className="text-brand-aqua">{c.sent_count}</Td>
                                        <Td className={c.failed_count ? 'text-brand-pink' : 'text-white/60'}>{c.failed_count}</Td>
                                        <Td>
                                            <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${c.status === 'sent' ? 'bg-brand-aqua/15 text-brand-aqua' : c.status === 'partial' ? 'bg-brand-gold/15 text-brand-gold' : c.status === 'skipped' ? 'bg-white/5 text-white/50' : c.status === 'sending' ? 'bg-brand-orange/15 text-brand-orange' : 'bg-brand-pink/15 text-brand-pink'}`}>{c.status}</span>
                                        </Td>
                                        <Td className="text-white/55 text-xs">{c.created_at ? new Date(c.created_at).toLocaleString() : '—'}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Stat({ k, v, c }) {
    return (
        <div className="bg-brand-surface border border-white/5 rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-white/50">{k}</div>
            <div className={`font-display text-3xl font-extrabold mt-2 ${c}`}>{v}</div>
        </div>
    );
}
function Field({ label, children }) {
    return <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-white/50">{label}</Label>{children}</div>;
}
const Th = ({ children }) => <th className="text-left px-5 py-3">{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-5 py-3 ${className}`}>{children}</td>;

function AdminLogin({ onSuccess }) {
    const [email, setEmail] = useState('rubiesglobalservices@gmail.com');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) return;
        setBusy(true);
        try {
            const { data } = await apiClient.post('/auth/admin/login', { email, password });
            toast.success(`Welcome back, ${data.user.name}!`);
            onSuccess(data.token);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Invalid email or password.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-5" data-testid="admin-login-page">
            <form onSubmit={submit} className="w-full max-w-md bg-brand-surface border border-white/10 rounded-3xl p-8 space-y-5">
                <div className="text-center">
                    <div className="text-xs uppercase tracking-[0.3em] text-brand-gold mb-2">Admin access</div>
                    <h1 className="font-display text-3xl uppercase tracking-tight">Ms. Ruby&apos;s control room</h1>
                    <p className="text-white/60 text-sm mt-2">Sign in to manage leads, reservations, and campaigns.</p>
                </div>
                <div className="space-y-4">
                    <Field label="Email">
                        <Input
                            data-testid="admin-login-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="bg-transparent border-white/15"
                        />
                    </Field>
                    <Field label="Password">
                        <Input
                            data-testid="admin-login-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="bg-transparent border-white/15"
                        />
                    </Field>
                </div>
                <Button
                    data-testid="admin-login-submit"
                    type="submit"
                    disabled={busy}
                    className="btn-sunset w-full rounded-full h-11 uppercase tracking-widest font-semibold"
                >
                    {busy ? 'Signing in…' : 'Sign In'}
                </Button>
            </form>
        </div>
    );
}
